import React, { useEffect, useState, forwardRef, ForwardedRef } from 'react';
import {
    Button,
    Container,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography,
    Box,
    useTheme,
    alpha,
    Stack,
    CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, LoginOutlined } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import ServiceAPI from '../../services/serviceapi';
import tokenService from '../../services/tokenservice';
import { SecUtils } from '../../utils/secutils';
import { RESPONSE } from '../../enums/response';

const Login = forwardRef((_props: unknown, _ref: ForwardedRef<any>) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { empresaParam } = useParams(); // Captura /login/:empresaParam
    const [searchParams] = useSearchParams();

    // Estados
    const [empresaID, setEmpresaID] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [senha, setSenha] = useState<string>('');
    const [erro, setErro] = useState<string>('');
    const [mostrarSenha, setMostrarSenha] = useState<boolean>(false);
    const [carregando, setCarregando] = useState<boolean>(false);
    const [checandoSessao, setChecandoSessao] = useState<boolean>(true);

    useEffect(() => {
        const inicializar = async () => {
            // 1. Verifica se já está logado
            const tkn = await tokenService.getToken();
            if (tkn) {
                navigate("/gestao");
                return;
            }

            // 2. Prioridade de preenchimento do EmpresaID
            // Primeiro olha a URL (/login/appcodesh), depois a query (?xs=)
            const idDaUrl = empresaParam || searchParams.get('xs');
            
            if (idDaUrl) {
                setEmpresaID(idDaUrl.toLowerCase().trim());
            }

            setChecandoSessao(false);
        };

        inicializar();
    }, [empresaParam, searchParams, navigate]);

    const podeLogar = () => {
        const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        return emailValido && empresaID.length >= 3 && senha.length >= 3 && !carregando;
    };

    const handleLogin = async () => {
        setCarregando(true);
        setErro('');

        try {
            const payload = {
                "e": SecUtils.encode(email),
                "p": SecUtils.encode(senha)
            };

            const response = await ServiceAPI.logar(empresaID, payload);

            if (response.codeResponse === RESPONSE.SUCESSO_LOGIN) {
                const tkn = response.dataResponse;
                tkn.empresaID = empresaID;
                await tokenService.saveToken(tkn);
                navigate("/gestao");
            } else {
                setErro("Credenciais inválidas ou erro na resposta.");
            }
        } catch (error: any) {
            const msg = error.response?.data?.message || error.message || "Erro ao realizar login";
            setErro(msg);
        } finally {
            setCarregando(false);
        }
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && podeLogar()) handleLogin();
    };

    if (checandoSessao) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={30} />
            </Box>
        );
    }

    return (
        <Box 
            sx={{ 
                minHeight: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: { xs: 2, sm: 0 },
                bgcolor: theme.palette.mode === 'dark' ? 'background.default' : alpha(theme.palette.primary.main, 0.02)
            }}
        >
            <Container maxWidth="xs" sx={{ p: 0 }}>
                <Paper 
                    elevation={0}
                    sx={{ 
                        p: { xs: 3, sm: 5 },
                        borderRadius: '28px',
                        border: `1px solid ${theme.palette.divider}`,
                        textAlign: 'center',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.04)'
                    }}
                >
                    <Box sx={{ mb: 4 }}>
                        <img src="/AppCodesh.png" alt="Logo" style={{ height: '35px', marginBottom: '16px' }} />
                        <Typography variant="h5" fontWeight={800} color="text.primary">
                            Acesse sua conta
                        </Typography>
                    </Box>

                    <Stack spacing={2}>
                        <TextField
                            fullWidth
                            label="ID da Empresa"
                            variant="outlined"
                            autoComplete="off" // Evita sugestões de e-mail neste campo
                            value={empresaID}
                            onChange={(e) => setEmpresaID(e.target.value.toLowerCase().replace(/\s/g, ''))}
                            InputProps={{ sx: { borderRadius: '12px' } }}
                        />

                        <TextField
                            fullWidth
                            label="Seu E-mail"
                            type="email"
                            variant="outlined"
                            autoComplete="username"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            InputProps={{ sx: { borderRadius: '12px' } }}
                        />

                        <TextField
                            fullWidth
                            label="Senha"
                            type={mostrarSenha ? 'text' : 'password'}
                            variant="outlined"
                            autoComplete="current-password"
                            value={senha}
                            onChange={(e) => setSenha(e.target.value)}
                            onKeyDown={onKeyDown}
                            error={!!erro}
                            helperText={erro}
                            InputProps={{
                                sx: { borderRadius: '12px' },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setMostrarSenha(!mostrarSenha)} edge="end">
                                            {mostrarSenha ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={handleLogin}
                            disabled={!podeLogar()}
                            startIcon={!carregando && <LoginOutlined />}
                            disableElevation
                            sx={{ 
                                py: 1.8, 
                                borderRadius: '12px', 
                                fontWeight: 700, 
                                textTransform: 'none',
                                mt: 1
                            }}
                        >
                            {carregando ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
                        </Button>
                    </Stack>

                    <Box sx={{ mt: 4 }}>
                        <Typography variant="caption" color="text.disabled">
                            AppCodesh &copy; {new Date().getFullYear()}
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
});

Login.displayName = 'Login';
export default Login;